// Copyright (c) StrangeBee 2024

import React, { ReactElement, useEffect, useMemo } from 'react';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import { computeEventList } from '@Organisms/PageTemplate/eventList';
import { PermissionsEnum, ObjectTypeV1 as ObjectType, PageTemplateInput, PageOutput } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { useCurrentUser } from '@Queries/userQueries';
import { PageTemplateManage } from '@Organisms/PageTemplate/PageTemplateManage/PageTemplateManage';
import useConfigList from '@Hooks/useConfigList';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { ICONS } from '@Constants/iconsApp';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { downloadFile } from '@Helpers/download';
import { THList } from '@Molecules/THList';
import TemplateImport from '@Organisms/TemplateImport/TemplateImport';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { Footer } from '@Molecules/THFilter/components/Footer';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { FormatMultipleDate } from '@Molecules/Format';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { useListAllFields } from '@Queries/describeQueries';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import DisplayEntityInfo from '@Organisms/DisplayEntityInfo/DisplayEntityInfo';
import { useDrawer } from '@Hooks/useDrawer';
import { RouteType } from '@Types';
import RouteBuilder from '@Services/RouteBuilder';
import usePageTitle from '@Hooks/usePageTitle';
import { useParams } from 'react-router';

import './PageTemplateList.scss';

export const PageTemplateList = (): ReactElement => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();
    const { visible: pageTemplatesVisible, setVisible: setPageTemplatesVisible } = useDrawer({});
    const { visible: pageTemplatesDefaultValuesVisible, setVisible: setPageTemplatesDefaultValuesVisible } = useDrawer(
        {},
    );
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_PAGE_TEMPLATE]);

    const config = useConfigList<PageOutput>({
        selector: {
            head: [{ name: 'listPageTemplate' }],
            name: 'get-all-page-templates',
            extraData: ['caseTemplateCount'],
        },
        controller: client.PageTemplateController,
        storageId: StorageFilterEnum.PAGE_TEMPLATE,
        objectType: ObjectType.PAGE_TEMPLATE,
    });

    const {
        dataSource: pageTemplateList = [],
        columnsRenderer,
        listRefetch,
        status = 'loading',
        paginationManager,
        table,
        storageViewManager,
        storagePageManager,
        filterManager,
    } = config;

    const { setDisplay, setOnLoadView } = storagePageManager || {};
    const { setDefaultDisplay } = storageViewManager || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.PageTemplateController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [listRefetch, i],
    );

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        _createdAt: { label: i('common.created-date'), visible: true },
        _updatedAt: { label: i('common.updated-date'), visible: true },
    });

    setOnLoadView?.((viewContent) => {
        loadVisibleStateDates({
            _createdAt: viewContent?.display?._createdAt ?? true,
            _updatedAt: viewContent?.display?._updatedAt ?? true,
        });
    });

    const columns = columnsRenderer([
        {
            title: [{ label: i('page-template.list.table.columns.category'), sortField: 'category' }],
            className: 'page-template-list__column__category column_preview',
            render: ({ category }): ReactElement => <div>{category}</div>,
        },
        {
            title: [{ label: i('page-template.list.table.columns.title'), sortField: 'title' }],
            className: 'page-template-list__column__title',
            render: ({ title }): ReactElement => <div>{title}</div>,
        },
        {
            title: i('page-template.list.table.columns.details'),
            className: 'page-template-list__column__details',
            render: ({ extraData }): ReactElement => (
                <>
                    <DisplayEntityInfo
                        count={extraData?.caseTemplateCount}
                        label={i('page-template.linked-case-template')}
                    />
                </>
            ),
        },
        {
            title: i('page-template.list.table.columns.by'),
            className: 'page-template-list__column__by',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} avatarOnly />,
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'C.', sortField: '_createdAt', hide: !showDates._createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates._updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'case-template-list__column__date',
            render: ({ _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates._createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates._updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'page-template-list__column__actions',
            render: (pageTemplate) => (
                <>
                    {hasPermissions.pagetemplate && (
                        <ActionsList
                            actionList={{
                                edit: {
                                    label: i('general.edit'),
                                    icon: <ICONS.EditIcon />,
                                    query: () => setPageTemplatesDefaultValuesVisible(true),
                                    CustomComponent: (triggerElem) => (
                                        <>
                                            {triggerElem}
                                            <PageTemplateManage
                                                isOnlyCreate
                                                defaultValues={pageTemplate}
                                                onCreate={(pageTemplate, id) => {
                                                    setPageTemplatesDefaultValuesVisible(false);
                                                    return eventList.onEdit(pageTemplate, id as string);
                                                }}
                                                pageTemplatesList={pageTemplateList}
                                                drawerTitle={i('page-template.actions.edit')}
                                                width={'60vw'}
                                                visible={pageTemplatesDefaultValuesVisible}
                                                setVisible={setPageTemplatesDefaultValuesVisible}
                                            />
                                        </>
                                    ),
                                },
                                export: {
                                    label: i('page-template.actions.export'),
                                    icon: <ICONS.ExportOutlined />,
                                    query: () =>
                                        downloadFile(
                                            `${pageTemplate.category}_${pageTemplate.title}.json`,
                                            JSON.stringify(pageTemplate),
                                            'application/json',
                                        ),
                                },
                                delete: {
                                    label: i('general.delete'),
                                    confirm: true,
                                    message: i('page-template.actions.delete.single'),
                                    icon: <ICONS.DeleteIcon />,
                                    query: () => eventList.onDelete(pageTemplate._id),
                                },
                            }}
                            isIcon
                            scope="single"
                        />
                    )}
                </>
            ),
        },
    ]);

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setDisplay?.('_createdAt', showDates._createdAt);
        setDisplay?.('_updatedAt', showDates._updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePageManager?.page?.currentView || '';

        setDefaultDisplay?.(currentView, {
            _createdAt: showDates._createdAt,
            _updatedAt: showDates._updatedAt,
        });
    }, [storagePageManager, showDates, setDefaultDisplay]);

    usePageTitle(i('page-title.organisation.templates.pages', { companyName: orgId }));

    return (
        <>
            <THLayout>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions.pagetemplate && (
                                <>
                                    <AddButton
                                        label={i('page-template.actions.add')}
                                        onClick={() => setPageTemplatesVisible(true)}
                                    />

                                    <TemplateImport
                                        mode="page-template"
                                        triggerElem={
                                            <THButton type="default">{i('page-template.import.label')}</THButton>
                                        }
                                        onCreate={(pageTemplate) =>
                                            eventList.onCreate(pageTemplate as unknown as PageTemplateInput)
                                        }
                                    />
                                </>
                            )}
                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            <ManageSwitch
                                label={i('common.filters')}
                                value={filterManager.editFilters}
                                onChange={(editFilters): void => {
                                    filterManager.setEditFilters(editFilters);
                                }}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                                <THFilter
                                    filterConfig={filterManager}
                                    describeField={describe?.[ObjectType.PAGE_TEMPLATE]}
                                >
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={pageTemplateList}
                        columns={columns}
                        rowKey="_id"
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('page-template.none')}>
                                {hasPermissions.pagetemplate && (
                                    <THButton type="link" onClick={() => setPageTemplatesVisible(true)}>
                                        {i('page-template.list.header.add-page-template')}
                                    </THButton>
                                )}
                            </DisplayEmptyData>
                        }
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>
            </THLayout>
            {pageTemplatesVisible && (
                <PageTemplateManage
                    drawerTitle={i('page-template.actions.create')}
                    isOnlyCreate
                    width={'60vw'}
                    onCreate={(pageTemplate) => eventList.onCreate(pageTemplate)}
                    visible={pageTemplatesVisible}
                    setVisible={setPageTemplatesVisible}
                />
            )}
        </>
    );
};

export const Route: RouteType = {
    content: <PageTemplateList />,
    path: RouteBuilder.compute('organisation.templates-page'),
    label: 'Page Templates',
    space: ['user'],
    menu: {
        label: 'organisation.layout.nav-bar.templates',
    },
    breadcrumb: { label: 'breadcrumb.organisation.page-templates' },
};
